import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { LineReveal } from "@/components/LineReveal";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { clearCart } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("unknown");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            cleared.current = true;
            clearCart();
          }
          return;
        }
      } catch {}
      attempts += 1;
      if (attempts < 12) setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
  }, [sessionId, clearCart]);

  return (
    <main data-testid="payment-success-page" className="bg-[#050505] text-white min-h-screen flex flex-col justify-center px-6 md:px-12">
      {status === "checking" && (
        <p data-testid="payment-checking" className="font-serif italic text-3xl text-white/60">
          Confirming your order…
        </p>
      )}
      {status === "paid" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
          <p className="text-[10px] tracking-[0.4em] uppercase text-white/40 mb-8">Payment Confirmed</p>
          <LineReveal
            immediate
            lines={["Yours.", "Welcome in."]}
            className="font-serif text-6xl md:text-8xl leading-[1.0] tracking-tight mb-12"
          />
          <p data-testid="payment-confirmed-message" className="text-sm text-white/60 max-w-md leading-relaxed mb-12">
            Your order is confirmed. Heavyweight cotton, on its way to your city.
          </p>
          <Link
            to="/shop"
            data-testid="success-continue-shopping"
            className="inline-block border border-white/40 px-8 py-4 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-white hover:text-black transition-colors duration-300"
          >
            Continue Shopping
          </Link>
        </motion.div>
      )}
      {(status === "pending" || status === "unknown") && (
        <div>
          <p data-testid="payment-pending-message" className="font-serif italic text-3xl text-white/60 mb-8">
            {status === "pending"
              ? "Payment is still processing — check your email for confirmation."
              : "We couldn't find that session."}
          </p>
          <Link
            to="/shop"
            data-testid="pending-back-to-shop"
            className="inline-block border border-white/40 px-8 py-4 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-white hover:text-black transition-colors duration-300"
          >
            Back to Shop
          </Link>
        </div>
      )}
    </main>
  );
}
